import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import AuthPage from "./components/auth/AuthPage";
import AdminDashboard from "./components/admin/AdminDashboard";
import GoogleSheetsIntegration from "./components/GoogleSheetsIntegration";
import GoogleSheetsSelection from "./components/GoogleSheetsSelection";
import CampaignBuilder from "./components/CampaignBuilder";
import CampaignList from "./components/CampaignList";
import MessageTemplates from "./components/MessageTemplates";
import WhatsAppIntegration from "./components/WhatsAppIntegration";
import WhatsAppCallback from "./components/WhatsAppCallback";
import RenewalCampaigns from "./components/RenewalCampaigns";
import CampaignAssistantPage from "./features/campaign-assistant/CampaignAssistantPage";
import CalendarIntegration from "./components/CalendarIntegration";
import GuidedTour from "./components/common/GuidedTour";
import { PaymentCheckoutPage, PaymentResultPage, usePayment } from "./features/payments";

import Report from "./components/Report";
import { InboxLayout } from "./modules/inbox/presentation/InboxLayout";
import GoogleCallback from "./components/google/GoogleCallback";
// Workflow components temporarily disabled for MVP
// import WorkflowList from "./components/WorkflowList";
// import WorkflowBuilder from "./components/WorkflowBuilder";
// import WorkflowTemplates from "./components/WorkflowTemplates";
import { Toaster } from "./components/ui/sonner";
import { LogOut, Shield, Zap, GraduationCap, AlertCircle } from "lucide-react";

// ✅ Importaciones para Mobile (Arquitectura SOLID)
import { MobileNavbar } from "./features/layout/components/MobileNavbar";
import { navigationConfig, adminNavigation } from "./features/layout/config/navigation";
import { getNavIcon } from "./features/layout/config/navIcons";
import { WelcomeOnboardingModal } from "./features/onboarding/components/WelcomeOnboardingModal";
import { NavigationItem, CurrentPage, AppContentState } from "./types";
import WhatsAppBubble from "./components/common/WhatsAppBubble";
import { useMetaAccountHealth } from "./hooks/useMetaAccountHealth";
import { captureEntryCode } from "./lib/signupEntry";
import { PAID_SIGNUP_PATH } from "./lib/authErrors";
import PaidSignupPage from "./features/paid-signup/PaidSignupPage";
import AutoExtendPage from "./components/auth/AutoExtendPage";
import { getPaidSignupStep } from "./features/paid-signup/paidSignupApi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

// Must run before any <Navigate> drops the query string.
captureEntryCode();

const DEFAULT_PAGE: CurrentPage = 'campaign-assistant';

// Ruta principal de cada página: se usa para navegar y para saber qué página mostrar según la URL.
const PAGE_ROUTES: Partial<Record<CurrentPage, string>> = {
  'campaign-assistant': '/campaign-assistant',
  'renewals': '/renewals',
  'report': '/report',
  'inbox': '/inbox',
  'templates': '/templates',
  'sheets': '/google-sheets',
  'calendar': '/calendar',
  'whatsapp': '/whatsapp',
  'payment': '/payment',
  'admin': '/admin',
  'campaigns': '/campaigns',
  'campaign-builder': '/campaign-builder',
};

// URLs adicionales que muestran una página existente pero no son su ruta principal.
const ROUTE_ALIASES: Record<string, CurrentPage> = {
  '/whatsapp/callback': 'whatsapp',
  '/dashboard': 'campaign-assistant',
  '/renewal-builder': 'renewals',
};

const PATH_TO_PAGE: Record<string, CurrentPage> = { ...ROUTE_ALIASES };
(Object.keys(PAGE_ROUTES) as CurrentPage[]).forEach((page) => {
  PATH_TO_PAGE[PAGE_ROUTES[page] as string] = page;
});

// Main App Content Component
function AppContent(): React.ReactElement {
  const { user, loading, isAuthenticated, isAdmin, logout } = useAuth();
  const { enabled: paymentsEnabled } = usePayment();
  const { hasCriticalMetaHealth } = useMetaAccountHealth();
  const location = useLocation();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState<CurrentPage>(
    () => PATH_TO_PAGE[window.location.pathname] || DEFAULT_PAGE
  );

  // Función para manejar la navegación
  const handleNavigation = (itemId: NavigationItem['id']): void => {
    const path = PAGE_ROUTES[itemId] || PAGE_ROUTES[DEFAULT_PAGE];
    navigate(path);
    setCurrentPage(PATH_TO_PAGE[path]);
  };

  // Sincroniza currentPage con la URL si el usuario navega manualmente
  useEffect(() => {
    const expectedPage = PATH_TO_PAGE[location.pathname];
    if (expectedPage && currentPage !== expectedPage) {
      setCurrentPage(expectedPage);
    }

    if (location.pathname === '/dashboard') {
      navigate('/campaign-assistant', { replace: true });
    }
  }, [location.pathname, currentPage, navigate]);

  const paidSignupStep = getPaidSignupStep(user);

  useEffect(() => {
    if (paidSignupStep) return;
    if (location.pathname === '/' || (isAuthenticated && (location.pathname === '/login' || location.pathname === '/register'))) {
      navigate('/campaign-assistant', { replace: true });
    }
  }, [location.pathname, navigate, isAuthenticated, paidSignupStep]);

  // Workflow state temporarily disabled for MVP
  // const [selectedWorkflow, setSelectedWorkflow] = useState(null);

  const [showUserMenu, setShowUserMenu] = useState<boolean>(false);
  const [sidebarCollapsed] = useState<boolean>(false);
  const [tourActive, setTourActive] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showUserMenu && !target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // Escucha el evento del GuidedTour para navegar a /whatsapp
  // GuidedTour (en PersistentGuidedTour) despacha 'tour-navigate-whatsapp'
  // cuando el usuario hace clic en "Siguiente" en el paso de bienvenida.
  useEffect(() => {
    const handler = () => handleNavigation('whatsapp');
    window.addEventListener('tour-navigate-whatsapp', handler);
    return () => window.removeEventListener('tour-navigate-whatsapp', handler);
  }, []); // eslint-disable-line

  // Check if we're on a special route that should render independently
  const isSpecialRoute = location.pathname === '/select-sheets' ||
    location.pathname === '/whatsapp/callback' ||
    location.pathname === '/google/callback' ||
    location.pathname === '/payment';

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full mx-4 text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Cargando...</h2>
          <p className="text-gray-600">Verificando tu sesión</p>
        </div>
        <Toaster />
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    if (location.pathname !== '/login' && location.pathname !== '/register') {
      return <Navigate to="/login" replace />;
    }
    return (
      <>
        <AuthPage />
        <Toaster />
        <WhatsAppBubble />
      </>
    );
  }

  // Campaign-URL signups stay on the paid signup until payment, business niche and email are done.
  if (paidSignupStep) {
    return <Navigate to={PAID_SIGNUP_PATH} replace />;
  }

  // Handle special routes that render independently
  if (isSpecialRoute) {
    return (
      <>
        {location.pathname === '/payment' && <PaymentCheckoutPage />}
        {location.pathname === '/select-sheets' && <GoogleSheetsSelection />}
        {location.pathname === '/whatsapp/callback' && <WhatsAppCallback />}
        {location.pathname === '/google/callback' && <GoogleCallback />}
        <Toaster />
        <WhatsAppBubble />
      </>
    );
  }

  const navigation: NavigationItem[] = [
    ...navigationConfig,
    ...(isAdmin() ? adminNavigation : []),
  ];

  const handleLogout = async (): Promise<void> => {
    await logout();
    setShowUserMenu(false);
  };

  const renderCurrentPage = (): React.ReactElement => {
    switch (currentPage) {
      case 'dashboard':
        return <RenewalCampaigns onCreateNew={() => handleNavigation('campaign-builder')} />;
      case 'renewals':
        return <RenewalCampaigns onCreateNew={() => handleNavigation('campaign-builder')} />;
      case 'admin':
        return isAdmin() ? <AdminDashboard /> : <RenewalCampaigns onCreateNew={() => handleNavigation('campaign-builder')} />;
      case 'sheets':
        return <GoogleSheetsIntegration />;
      case 'whatsapp':
        return <WhatsAppIntegration />;
      case 'report':
        return <Report />;
      case 'calendar':
        return <CalendarIntegration />;
      case 'inbox':
        return <InboxLayout />;
      case 'campaigns':
        return <CampaignList onCreateNew={() => handleNavigation('campaign-builder')} />;
      case 'campaign-builder':
        return <CampaignBuilder onBack={() => handleNavigation('campaigns')} />;
      case 'campaign-assistant':
        return <CampaignAssistantPage />;
      case 'templates':
        return <MessageTemplates />;
      default:
        return <RenewalCampaigns onCreateNew={() => handleNavigation('campaign-builder')} />;
    }
  };

  return (
    /* agregar padding-top igual a la altura del header (h-20) para que el contenido no quede oculto */
    <div className="min-h-screen bg-gray-100 pt-20 app-container">
      {/* Header (aumentado) */}
      <header className="bg-white shadow-sm border-b fixed top-0 left-0 right-0 z-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              {/* ✅ Nuevo Menú Hamburguesa SOLID (Solo visible en Móvil) */}
              <MobileNavbar
                currentPage={currentPage}
                onNavigate={handleNavigation}
              />

              {/* Logo a la izquierda (más grande) */}
              <div className="flex items-center mr-4">
                <a href="/campaign-assistant">
                  <img
                    src="/assets/logo_reveca_def.png"
                    alt="Reveca Logo"
                    className="h-10 sm:h-12 w-auto object-contain"
                  />
                </a>
              </div>
            </div>

            <div className="ml-auto flex items-center space-x-3">
              {/* Tour toggle button */}
              {process.env.REACT_APP_ENABLE_TOURS !== 'false' && process.env.REACT_APP_ENABLE_TOURS !== '0' && (
                (() => {
                  const isPreEnabled = process.env.REACT_APP_ENABLE_PRE_TOUR !== 'false' && process.env.REACT_APP_ENABLE_PRE_TOUR !== '0';
                  const isPostEnabled = process.env.REACT_APP_ENABLE_POST_TOUR !== 'false' && process.env.REACT_APP_ENABLE_POST_TOUR !== '0';
                  
                  // Solo ocultamos el botón si AMBOS flujos están desactivados.
                  // Si uno de los dos sigue activo, el botón debe permitir lanzar las guías manuales de esa fase.
                  if (!isPreEnabled && !isPostEnabled) return null;

                  return (
                    <button
                      onClick={() => {
                        // Comunica con PersistentGuidedTour vía CustomEvent (GuidedTour
                        // vive fuera de AppContent para sobrevivir los cambios de ruta)
                        window.dispatchEvent(new CustomEvent('toggle-guided-tour'));
                      }}
                      title={tourActive ? 'Detener guía' : 'Iniciar guía paso a paso'}
                      className={`relative flex items-center justify-center w-9 h-9 rounded-full transition-all duration-200 ${tourActive
                          ? 'bg-teal-100 text-teal-700 ring-2 ring-teal-400 ring-offset-1'
                          : 'bg-gray-100 text-gray-500 hover:bg-teal-50 hover:text-teal-600'
                        }`}
                    >
                      <GraduationCap className="h-5 w-5" />
                      {tourActive && (
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-teal-500 rounded-full animate-pulse border border-white" />
                      )}
                    </button>
                  );
                })()
              )}
              {/* User info and menu (avatar más grande) */}
              <div className="relative user-menu-container">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 md:space-x-3 text-sm rounded-full bg-gray-50 px-2 md:px-3 py-2 hover:bg-gray-100 transition-colors"
                >
                  <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                    </span>
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-gray-900 font-medium text-xs md:text-sm">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-gray-500 text-xs flex items-center">
                      {isAdmin() && <Shield className="h-3 w-3 mr-1" />}
                      {user?.role === 'admin' ? 'Administrador' : 'Operador'}
                    </p>
                  </div>
                  {/* Solo mostrar iniciales en móvil */}
                  <div className="text-left sm:hidden">
                    <p className="text-gray-900 font-medium text-xs">
                      {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                    </p>
                  </div>
                </button>

                {/* User dropdown menu */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-64 sm:w-72 bg-white rounded-lg shadow-lg border py-1 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm text-gray-900 font-medium truncate">
                        {user?.first_name} {user?.last_name}
                      </p>
                      <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                    </div>

                    {paymentsEnabled && (
                      <div className="border-t border-gray-100 mt-1 pt-1">
                        <button
                          onClick={() => {
                            navigate('/payment/checkout');
                            setShowUserMenu(false);
                          }}
                          className="flex items-center w-full px-4 py-2 text-sm text-teal-700 hover:bg-teal-50 font-medium"
                        >
                          <Zap className="h-4 w-4 mr-3" />
                          Recargar Saldo
                        </button>
                      </div>
                    )}

                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                      >
                        <LogOut className="h-4 w-4 mr-3" />
                        Cerrar Sesión
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <div
        className={`max-w-full mx-auto ${
          currentPage === "inbox" ? "px-0 py-0 md:px-6 lg:px-8 md:py-8" : "px-4 sm:px-6 lg:px-8 py-8"
        }`}
      >
        <div className="flex space-x-0 md:space-x-4">
          {/* Desktop Sidebar */}
          <nav
            className={`hidden md:block bg-white rounded-lg shadow-sm border transition-all duration-300 ${sidebarCollapsed ? 'w-16' : 'w-64'} flex-shrink-0 sticky self-start overflow-auto z-20`}
            style={{
              // header h-20 = 5rem. py-8 del contenedor = 2rem arriba y 2rem abajo
              top: '7rem',  // 5rem (header) + 2rem (padding top del contenedor)
              // Altura fija: viewport - header (5rem) - padding top (2rem) - padding bottom (2rem)
              height: 'calc(100vh - 5rem - 4rem)',
            }}
          >
            <div className="p-4">
              {/* Principales (PRIMERO) */}
              <div className="mb-6">
                <div className={`text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-3 px-3 transition-all duration-200 ${sidebarCollapsed ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100 h-auto'
                  }`}>
                  Principal
                </div>
                <ul className="space-y-1">
                  {navigation.filter(item => item.category === 'main').map((item) => (
                    <li key={item.id}>
                      <button
                        id={`nav-${item.id}`}
                        onClick={() => handleNavigation(item.id)}
                        className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'justify-start px-3'} py-2.5 rounded-lg text-left transition-all duration-200 ${currentPage === item.id
                          ? 'bg-[#E8F7F7] text-[#0D7377] border-l-[3px] border-[#1CB3B3] rounded-l-none'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        title={sidebarCollapsed ? item.name : ''}
                      >
                        <span className={`relative ${currentPage === item.id ? 'text-[#1CB3B3]' : 'text-gray-400'}`}>
                          {getNavIcon(item.icon)}
                          {item.id === 'whatsapp' && hasCriticalMetaHealth && sidebarCollapsed && (
                            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                          )}
                        </span>
                        {!sidebarCollapsed && (
                          <>
                            <span className="font-medium ml-3 text-sm">{item.name}</span>
                            {item.id === 'whatsapp' && hasCriticalMetaHealth && (
                              <AlertCircle className="h-3.5 w-3.5 text-red-500 animate-pulse ml-1.5 shrink-0" aria-hidden />
                            )}
                          </>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Componentes (SEGUNDO) */}
              <div className="mb-6">
                <div className={`text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-3 px-3 transition-all duration-200 ${sidebarCollapsed ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100 h-auto'
                  }`}>
                  Componentes
                </div>
                <ul className="space-y-1">
                  {navigation.filter(item => item.category === 'components').map((item) => (
                    <li key={item.id}>
                      <button
                        id={`nav-${item.id}`}
                        onClick={() => handleNavigation(item.id)}
                        className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'justify-start px-3'} py-2.5 rounded-lg text-left transition-all duration-200 ${currentPage === item.id
                          ? 'bg-[#E8F7F7] text-[#0D7377] border-l-[3px] border-[#1CB3B3] rounded-l-none'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        title={sidebarCollapsed ? item.name : ''}
                      >
                        <span className={`relative ${currentPage === item.id ? 'text-[#1CB3B3]' : 'text-gray-400'}`}>
                          {getNavIcon(item.icon)}
                          {item.id === 'whatsapp' && hasCriticalMetaHealth && sidebarCollapsed && (
                            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                          )}
                        </span>
                        {!sidebarCollapsed && (
                          <>
                            <span className="font-medium ml-3 text-sm">{item.name}</span>
                            {item.id === 'whatsapp' && hasCriticalMetaHealth && (
                              <AlertCircle className="h-3.5 w-3.5 text-red-500 animate-pulse ml-1.5 shrink-0" aria-hidden />
                            )}
                          </>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Configuración (TERCERO) */}
              <div className="mb-6">
                <div className={`text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-3 px-3 transition-all duration-200 ${sidebarCollapsed ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100 h-auto'
                  }`}>
                  Configuración
                </div>
                <ul className="space-y-1">
                  {navigation.filter(item => item.category === 'config').map((item) => (
                    <li key={item.id}>
                      <button
                        id={`nav-${item.id}`}
                        onClick={() => handleNavigation(item.id)}
                        className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'justify-start px-3'} py-2.5 rounded-lg text-left transition-all duration-200 ${currentPage === item.id
                          ? 'bg-[#E8F7F7] text-[#0D7377] border-l-[3px] border-[#1CB3B3] rounded-l-none'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        title={sidebarCollapsed ? item.name : ''}
                      >
                        <span className={`relative ${currentPage === item.id ? 'text-[#1CB3B3]' : 'text-gray-400'}`}>
                          {getNavIcon(item.icon)}
                          {item.id === 'whatsapp' && hasCriticalMetaHealth && sidebarCollapsed && (
                            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                          )}
                        </span>
                        {!sidebarCollapsed && (
                          <>
                            <span className="font-medium ml-3 text-sm">{item.name}</span>
                            {item.id === 'whatsapp' && hasCriticalMetaHealth && (
                              <AlertCircle className="h-3.5 w-3.5 text-red-500 animate-pulse ml-1.5 shrink-0" aria-hidden />
                            )}
                          </>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pagos (CUARTO) */}
              <div className="mb-6">
                <div className={`text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-3 px-3 transition-all duration-200 ${sidebarCollapsed ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100 h-auto'
                  }`}>
                  Pagos
                </div>
                <ul className="space-y-1">
                  {navigation.filter(item => item.category === 'payment').map((item) => (
                    <li key={item.id}>
                      <button
                        id={`nav-${item.id}`}
                        onClick={() => handleNavigation(item.id)}
                        className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'justify-start px-3'} py-2.5 rounded-lg text-left transition-all duration-200 ${currentPage === item.id
                          ? 'bg-[#E8F7F7] text-[#0D7377] border-l-[3px] border-[#1CB3B3] rounded-l-none'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        title={sidebarCollapsed ? item.name : ''}
                      >
                        <span className={`relative ${currentPage === item.id ? 'text-[#1CB3B3]' : 'text-gray-400'}`}>
                          {getNavIcon(item.icon)}
                          {item.id === 'whatsapp' && hasCriticalMetaHealth && sidebarCollapsed && (
                            <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                          )}
                        </span>
                        {!sidebarCollapsed && (
                          <>
                            <span className="font-medium ml-3 text-sm">{item.name}</span>
                            {item.id === 'whatsapp' && hasCriticalMetaHealth && (
                              <AlertCircle className="h-3.5 w-3.5 text-red-500 animate-pulse ml-1.5 shrink-0" aria-hidden />
                            )}
                          </>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Admin section (ÚLTIMO) */}
              {isAdmin() && (
                <div>
                  <div className={`text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-3 px-3 transition-all duration-200 ${sidebarCollapsed ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100 h-auto'
                    }`}>
                    Administración
                  </div>
                  <ul className="space-y-1">
                    {navigation.filter(item => item.category === 'admin').map((item) => (
                      <li key={item.id}>
                        <button
                          id={`nav-${item.id}`}
                          onClick={() => handleNavigation(item.id)}
                          className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'justify-start px-3'} py-2.5 rounded-lg text-left transition-all duration-200 ${currentPage === item.id
                            ? 'bg-[#E8F7F7] text-[#0D7377] border-l-[3px] border-[#1CB3B3] rounded-l-none'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          title={sidebarCollapsed ? item.name : ''}
                        >
                          <span className={currentPage === item.id ? 'text-[#1CB3B3]' : 'text-gray-400'}>{getNavIcon(item.icon)}</span>
                          {!sidebarCollapsed && (
                            <span className="font-medium ml-3 text-sm">{item.name}</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </nav>

          {/* Main Content */}
          <div
            className={`flex-1 min-w-0 w-full md:w-auto transition-all duration-200 ${
              sidebarCollapsed ? "md:ml-16" : "md:ml-64"
            } ${
              currentPage === "campaign-assistant"
                ? "overflow-hidden h-[calc(100vh-5rem-4rem)] min-h-0 flex flex-col"
                : ""
            }`}
          >
            {renderCurrentPage()}
          </div>
        </div>
      </div>

      <Toaster />
      <WhatsAppBubble />

      {/* ✅ Modal de Onboarding Unificado (Mobile & Web) */}
      <WelcomeOnboardingModal />
    </div>
  );
}

// ── Persistent tour: vive FUERA de AppContent para sobrevivir cambios de ruta ──
// AppContent se destruye y recrea en cada navegación. GuidedTour aquí NO.
// El botón 🎓 en AppContent usa window CustomEvent para comunicar.
function PersistentGuidedTour(): React.ReactElement | null {
  const { user } = useAuth();
  const [tourActive, setTourActive] = React.useState<boolean | undefined>(undefined);

  // Si las guías están deshabilitadas por variables de entorno, no renderizar nada.
  if (process.env.REACT_APP_ENABLE_TOURS === 'false' || process.env.REACT_APP_ENABLE_TOURS === '0') {
    return null;
  }

  React.useEffect(() => {
    const toggleHandler = () => setTourActive(prev => !prev);
    const stopHandler = () => setTourActive(false);
    window.addEventListener('toggle-guided-tour', toggleHandler);
    window.addEventListener('guided-tour-ended', stopHandler);
    return () => {
      window.removeEventListener('toggle-guided-tour', toggleHandler);
      window.removeEventListener('guided-tour-ended', stopHandler);
    };
  }, []);

  // No mostrar el tour durante el proceso de logueo ni mientras el registro con pago esté incompleto
  if (!user || getPaidSignupStep(user)) return null;

  return (
    <GuidedTour
      userId={user.id}
      hasSeenOnboarding={user.has_seen_onboarding}
      forceStart={tourActive}
      onTourEnd={() => {
        setTourActive(false);
        window.dispatchEvent(new CustomEvent('guided-tour-ended'));
      }}
    />
  );
}

// Main App component with Router and Auth Provider
function App(): React.ReactElement {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/google-sheets" element={<AppContent />} />
          <Route path="/whatsapp" element={<AppContent />} />
          <Route path="/campaign-assistant" element={<AppContent />} />
          <Route path="/renewals" element={<AppContent />} />
          <Route path="/renewal-builder" element={<AppContent />} />
          <Route path="/register" element={<AppContent />} />
          {/* T08 auto-extend landing (email / WhatsApp link) */}
          <Route path="/auto-extend" element={<AutoExtendPage />} />
          {/* Registro con pago (URL de campaña) - standalone */}
          <Route path={PAID_SIGNUP_PATH} element={<PaidSignupPage />} />
          <Route path="/whatsapp/callback" element={<AppContent />} />
          <Route path="/google/callback" element={<AppContent />} />
          <Route path="/select-sheets" element={<AppContent />} />
          {/* Legacy /dashboard → Asistente (bookmarks / links viejos) */}
          <Route path="/dashboard" element={<AppContent />} />
          <Route path="/campaigns" element={<AppContent />} />
          <Route path="/campaign-builder" element={<AppContent />} />
          <Route path="/templates" element={<AppContent />} />
          <Route path="/admin" element={<AppContent />} />
          <Route path="/report" element={<AppContent />} />
          <Route path="/calendar" element={<AppContent />} />
          {/* Pasarela de pagos ePayco - standalone (sin sidebar) */}
          <Route path="/payment/checkout" element={<PaymentCheckoutPage />} />
          <Route path="/payment/result" element={<PaymentResultPage />} />
          <Route path="/*" element={<AppContent />} />
        </Routes>
        {/* PersistentGuidedTour vive aquí, fuera de las rutas, para no desmontarse */}
        <PersistentGuidedTour />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;